import { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, Menu, X, LogOut, User as UserIcon, Settings, Crown, Shield, ChevronDown, Trophy, Zap, Sparkles, Palette, Users } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useCredits } from "../../context/CreditsContext";
import JellyButton from "../common/JellyButton";

const Navbar = () => {
  const { user, logout } = useAuth();
  const { credits, creditData } = useCredits();
  const [menuOpen, setMenuOpen] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const [creditsDropdownOpen, setCreditsDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Close mobile menu when route changes
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const featureLinks = [
    { label: "How It Works", to: "/how-it-works" },
    { label: "AI Illustrations", to: "/ai-illustrations" },
    { label: "Custom Characters", to: "/custom-characters" },
    { label: "Art Styles", to: "/art-styles" },
    { label: "Reading Experience", to: "/reading-experience" }
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-violet-600 flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
            StoryWeaver
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-6">
          {/* Features Dropdown */}
          <div className="relative group">
            <button className="flex items-center gap-1 text-slate-600 hover:text-slate-900 font-medium">
              Features <ChevronDown className="w-4 h-4" />
            </button>
            <div className="absolute left-0 top-full mt-2 py-2 w-56 bg-white rounded-xl shadow-lg border border-slate-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
              {featureLinks.map(link => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="block px-4 py-2 text-slate-600 hover:bg-violet-50 hover:text-violet-600"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
          
          <Link to="/for-parents" className="text-slate-600 hover:text-slate-900 font-medium">
            For Parents
          </Link>
          <Link to="/pricing" className="text-slate-600 hover:text-slate-900 font-medium">
            Pricing
          </Link>
          <Link to="/marketplace" className="text-slate-600 hover:text-slate-900 font-medium">
            Marketplace
          </Link>
          <Link to="/blog" className="text-slate-600 hover:text-slate-900 font-medium">
            Blog
          </Link>
          <Link to="/tutorials" className="text-slate-600 hover:text-slate-900 font-medium">
            Tutorials
          </Link>
          
          {user ? (
            <>
              <Link to="/dashboard" className="text-slate-600 hover:text-slate-900 font-medium">
                Dashboard
              </Link>
              
              {/* Credit Balance - Enhanced with dropdown */}
              <div className="relative group">
                <Link 
                  to="/subscription" 
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-violet-100 to-amber-100 rounded-full hover:from-violet-200 hover:to-amber-200 transition-all"
                  onMouseEnter={() => setCreditsDropdownOpen(true)}
                  onMouseLeave={() => setCreditsDropdownOpen(false)}
                >
                  <Zap className="w-4 h-4 text-amber-600" />
                  <span className="font-bold text-slate-700">
                    {credits !== null ? credits : '...'}
                  </span>
                  <span className="text-xs text-slate-500">credits</span>
                </Link>
                
                {/* Credits breakdown dropdown */}
                <div 
                  className={`absolute right-0 top-full mt-2 py-3 px-4 w-56 bg-white rounded-xl shadow-lg border border-slate-100 transition-all ${
                    creditsDropdownOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
                  }`}
                  onMouseEnter={() => setCreditsDropdownOpen(true)}
                  onMouseLeave={() => setCreditsDropdownOpen(false)}
                >
                  <p className="text-xs font-semibold text-slate-500 mb-2">Credit Breakdown</p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <BookOpen className="w-3.5 h-3.5 text-violet-500" />
                        <span className="text-sm text-slate-600">Storybook</span>
                      </div>
                      <span className="text-sm font-bold text-slate-700">{creditData?.storybook || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Users className="w-3.5 h-3.5 text-emerald-500" />
                        <span className="text-sm text-slate-600">Character</span>
                      </div>
                      <span className="text-sm font-bold text-slate-700">{creditData?.character || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Palette className="w-3.5 h-3.5 text-pink-500" />
                        <span className="text-sm text-slate-600">Art Style</span>
                      </div>
                      <span className="text-sm font-bold text-slate-700">{creditData?.art_style || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                        <span className="text-sm text-slate-600">General</span>
                      </div>
                      <span className="text-sm font-bold text-slate-700">{(creditData?.general || 0) + (creditData?.bonus || 0)}</span>
                    </div>
                  </div>
                  <div className="border-t border-slate-100 mt-2 pt-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-700">Total</span>
                      <span className="text-sm font-bold text-violet-600">{credits || 0}</span>
                    </div>
                  </div>
                  <Link 
                    to="/subscription" 
                    className="block mt-3 text-center text-xs text-violet-600 hover:text-violet-700 font-medium"
                  >
                    Manage Subscription →
                  </Link>
                </div>
              </div>
              
              <div className="relative group">
                <button className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center">
                      <UserIcon className="w-4 h-4 text-violet-600" />
                    </div>
                  )}
                </button>
                <div className="absolute right-0 top-full mt-2 py-2 w-48 bg-white rounded-xl shadow-lg border border-slate-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                  <div className="px-4 py-2 border-b border-slate-100">
                    <p className="font-medium text-slate-900">{user.name}</p>
                    <p className="text-sm text-slate-500 truncate">{user.email}</p>
                  </div>
                  <Link
                    to="/settings"
                    className="w-full px-4 py-2 text-left text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                  >
                    <Settings className="w-4 h-4" /> Settings
                  </Link>
                  <Link
                    to="/achievements"
                    className="w-full px-4 py-2 text-left text-amber-600 hover:bg-amber-50 flex items-center gap-2"
                  >
                    <Trophy className="w-4 h-4" /> Achievements
                  </Link>
                  <Link
                    to="/subscription"
                    className="w-full px-4 py-2 text-left text-violet-600 hover:bg-violet-50 flex items-center gap-2"
                  >
                    <Crown className="w-4 h-4" /> Subscription
                  </Link>
                  <Link
                    to="/parental-controls"
                    className="w-full px-4 py-2 text-left text-green-600 hover:bg-green-50 flex items-center gap-2"
                  >
                    <Shield className="w-4 h-4" /> Parental Controls
                  </Link>
                  {user.is_master_admin && (
                    <Link
                      to="/admin"
                      className="w-full px-4 py-2 text-left text-purple-600 hover:bg-purple-50 flex items-center gap-2 font-medium"
                    >
                      <Shield className="w-4 h-4" /> Admin Dashboard
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-2 text-left text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <LogOut className="w-4 h-4" /> Sign Out
                  </button>
                </div>
              </div>
            </>
          ) : (
            <JellyButton variant="primary" onClick={() => navigate("/login")}>
              Get Started
            </JellyButton>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2 text-slate-600"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-slate-100"
          >
            <div className="p-4 space-y-3">
              {/* Features Dropdown - Mobile */}
              <div>
                <button 
                  onClick={() => setFeaturesOpen(!featuresOpen)}
                  className="flex items-center justify-between w-full py-2 text-slate-600 font-medium"
                >
                  <span>Features</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${featuresOpen ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {featuresOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="pl-4 space-y-1 overflow-hidden"
                    >
                      {featureLinks.map(link => (
                        <Link
                          key={link.to}
                          to={link.to}
                          className="block py-2 text-slate-500 hover:text-violet-600"
                          onClick={() => setMenuOpen(false)}
                        >
                          {link.label}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              <Link to="/for-parents" className="block py-2 text-slate-600 font-medium" onClick={() => setMenuOpen(false)}>For Parents</Link>
              <Link to="/pricing" className="block py-2 text-slate-600 font-medium" onClick={() => setMenuOpen(false)}>Pricing</Link>
              <Link to="/marketplace" className="block py-2 text-slate-600" onClick={() => setMenuOpen(false)}>Marketplace</Link>
              <Link to="/blog" className="block py-2 text-slate-600" onClick={() => setMenuOpen(false)}>Blog</Link>
              <Link to="/tutorials" className="block py-2 text-slate-600" onClick={() => setMenuOpen(false)}>Tutorials</Link>
              {user ? (
                <>
                  <Link to="/dashboard" className="block py-2 text-slate-600" onClick={() => setMenuOpen(false)}>Dashboard</Link>
                  
                  {/* Mobile Credit Balance - Enhanced */}
                  <Link 
                    to="/subscription" 
                    className="block py-2"
                    onClick={() => setMenuOpen(false)}
                  >
                    <div className="px-3 py-3 bg-gradient-to-r from-violet-100 to-amber-100 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-700">Credits</span>
                        <span className="font-bold text-violet-600">{credits !== null ? credits : '...'} total</span>
                      </div>
                      <div className="grid grid-cols-4 gap-2 text-center">
                        <div className="bg-white/50 rounded-lg p-1.5">
                          <BookOpen className="w-3 h-3 text-violet-500 mx-auto mb-0.5" />
                          <span className="text-xs font-bold text-slate-700">{creditData?.storybook || 0}</span>
                          <p className="text-[10px] text-slate-500">Story</p>
                        </div>
                        <div className="bg-white/50 rounded-lg p-1.5">
                          <Users className="w-3 h-3 text-emerald-500 mx-auto mb-0.5" />
                          <span className="text-xs font-bold text-slate-700">{creditData?.character || 0}</span>
                          <p className="text-[10px] text-slate-500">Char</p>
                        </div>
                        <div className="bg-white/50 rounded-lg p-1.5">
                          <Palette className="w-3 h-3 text-pink-500 mx-auto mb-0.5" />
                          <span className="text-xs font-bold text-slate-700">{creditData?.art_style || 0}</span>
                          <p className="text-[10px] text-slate-500">Style</p>
                        </div>
                        <div className="bg-white/50 rounded-lg p-1.5">
                          <Sparkles className="w-3 h-3 text-amber-500 mx-auto mb-0.5" />
                          <span className="text-xs font-bold text-slate-700">{(creditData?.general || 0) + (creditData?.bonus || 0)}</span>
                          <p className="text-[10px] text-slate-500">Gen</p>
                        </div>
                      </div>
                    </div>
                  </Link>
                  
                  <Link to="/achievements" className="block py-2 text-amber-600 font-medium flex items-center gap-2" onClick={() => setMenuOpen(false)}>
                    <Trophy className="w-4 h-4" /> Achievements
                  </Link>
                  <Link to="/subscription" className="block py-2 text-violet-600 font-medium flex items-center gap-2" onClick={() => setMenuOpen(false)}>
                    <Crown className="w-4 h-4" /> Subscription
                  </Link>
                  {user.is_master_admin && (
                    <Link to="/admin" className="block py-2 text-purple-600 font-medium flex items-center gap-2" onClick={() => setMenuOpen(false)}>
                      <Shield className="w-4 h-4" /> Admin Dashboard
                    </Link>
                  )}
                  <Link to="/settings" className="block py-2 text-slate-600" onClick={() => setMenuOpen(false)}>Settings</Link>
                  <button onClick={() => { handleLogout(); setMenuOpen(false); }} className="block py-2 text-red-600">Sign Out</button>
                </>
              ) : (
                <JellyButton variant="primary" className="w-full" onClick={() => { navigate("/login"); setMenuOpen(false); }}>
                  Get Started
                </JellyButton>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
